// WhatsApp Service Frontend para CentralGym
// Se conecta al Backend API Node.js que maneja whatsapp-web.js

import axios from 'axios';
import io from 'socket.io-client';
import { getWhatsAppApiConfig, validateWhatsAppApiConfig, debugWhatsAppApiConfig } from '@/config/whatsappApiConfig';

console.log('🔄 Cargando WhatsApp Service...');

// Verificar que las dependencias se cargaron correctamente
try {
  console.log('📦 Verificando dependencias...');
  console.log('✅ Axios disponible:', !!axios);
  console.log('✅ Socket.IO disponible:', !!io);
  console.log('✅ Configuración disponible');
} catch (verifyError) {
  console.error('❌ Error verificando dependencias:', verifyError);
}

// Obtener configuración
const config = getWhatsAppApiConfig();

// Validar configuración al cargar
try {
  validateWhatsAppApiConfig();
  console.log('✅ Configuración WhatsApp API validada correctamente');
} catch (error) {
  console.error('❌ Error en configuración WhatsApp API:', error.message);
}

// Debug de configuración en desarrollo
if (config.debug.enabled) {
  debugWhatsAppApiConfig();
}

// Configuración del cliente Socket.IO con reconexión automática
let socket;
try {
  console.log('🔌 Inicializando Socket.IO con configuración:', {
    baseUrl: config.apiBaseUrl,
    socketConfig: config.socketConfig
  });
  
  socket = io(config.apiBaseUrl, {
    ...config.socketConfig,
    // Configuración adicional para mantener conexión persistente
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    forceNew: false
  });
  
  console.log('✅ Socket.IO inicializado correctamente');
  console.log('🔌 Socket instance:', !!socket);
  console.log('🔌 Socket opts:', socket.opts ? 'Disponible' : 'No disponible');
  
} catch (error) {
  console.error('❌ Error inicializando Socket.IO:', error);
  // Crear un objeto socket mock para evitar errores
  socket = {
    on: () => {},
    connect: () => {},
    disconnect: () => {},
    connected: false,
    disconnected: true,
    id: null,
    opts: {}
  };
}

class WhatsAppService {
  constructor() {
    console.log('🚀 Inicializando WhatsAppService...');
    
    try {
      this.apiBaseUrl = config.apiBaseUrl;
      this.config = config;
      this.isReady = false;
      this.isConnecting = false;
      this.qrCode = null;
      this.eventCallbacks = {};
      this.connectionStatus = 'disconnected'; // disconnected, connecting, connected, error
      this.lastError = null;
      this.messageQueue = [];
      this.isProcessingQueue = false;
      this.socket = socket;
      
      console.log('✅ Propiedades básicas inicializadas');
      console.log('🔧 WhatsApp Service inicializado con configuración:', {
        apiBaseUrl: this.apiBaseUrl,
        debug: this.config.debug.enabled,
        socketAvailable: !!this.socket
      });
      
      console.log('🔌 Configurando Socket listeners...');
      this.setupSocketListeners();
      console.log('✅ Socket listeners configurados');
      
      console.log('🔍 Iniciando monitoreo...');
      // Iniciar monitoreo de conexión
      this.startConnectionMonitoring();
      console.log('✅ Monitoreo iniciado');
      
      console.log('🎉 WhatsAppService inicializado completamente');
      
    } catch (error) {
      console.error('❌ Error en constructor de WhatsAppService:', error);
      console.error('❌ Stack trace:', error.stack);
      
      // Inicializar valores por defecto en caso de error
      this.apiBaseUrl = 'http://localhost:3001';
      this.config = { debug: { enabled: false } };
      this.isReady = false;
      this.isConnecting = false;
      this.qrCode = null;
      this.eventCallbacks = {};
      this.connectionStatus = 'error';
      this.lastError = error.message;
      this.messageQueue = [];
      this.isProcessingQueue = false;
      this.socket = socket; // usar el socket mock si es necesario
    }
  }

  // Configurar listeners de Socket.IO
  setupSocketListeners() {
    console.log('🔌 Configurando listeners de Socket.IO...');
    console.log('🔌 Socket.IO configuración:', {
      url: this.apiBaseUrl,
      autoConnect: this.socket?.opts?.autoConnect || 'N/A',
      reconnection: this.socket?.opts?.reconnection || 'N/A',
      reconnectionAttempts: this.socket?.opts?.reconnectionAttempts || 'N/A'
    });
    
    // Eventos de conexión Socket.IO con logging detallado
    this.socket.on('connect', () => {
      console.log('🔌 Socket.IO conectado exitosamente');
      console.log('🔌 Socket ID:', this.socket.id);
      console.log('🔌 Timestamp:', new Date().toISOString());
      console.log('🔌 Estado del socket:', {
        connected: this.socket.connected,
        disconnected: this.socket.disconnected
      });
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('🔌 Socket.IO desconectado');
      console.warn('🔌 Razón de desconexión:', reason);
      console.warn('🔌 Timestamp:', new Date().toISOString());
      console.warn('🔌 ¿Se reconectará automáticamente?', this.socket?.opts?.reconnection || 'Unknown');
      
      // Si la desconexión es involuntaria, intentar reconectar manualmente
      if (reason === 'io server disconnect') {
        console.log('🔄 Desconexión del servidor, reconectando...');
        this.socket.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Error de conexión Socket.IO:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Timestamp:', new Date().toISOString());
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket.IO reconectado exitosamente');
      console.log('🔄 Intento número:', attemptNumber);
      console.log('🔄 Timestamp:', new Date().toISOString());
      console.log('🔄 Socket ID:', this.socket.id);
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('🔄 Intentando reconectar Socket.IO...');
      console.log('🔄 Intento número:', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('❌ Error en reconexión Socket.IO:', error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Falló la reconexión Socket.IO después de todos los intentos');
    });

    // Eventos de estado WhatsApp - Mejorados para múltiples eventos QR
    this.socket.on('qr-code', (data) => {
      console.log('📱 QR Code recibido del backend (evento qr-code):', data);
      console.log('📱 ID Empresa:', data.idEmpresa);
      console.log('📱 QR Data exists:', !!data.qrCode);
      console.log('📱 QR Data type:', typeof data.qrCode);
      console.log('📱 QR Data length:', data.qrCode ? data.qrCode.length : 0);
      
      if (data.qrCode) {
        console.log('📱 QR Code preview:', data.qrCode.substring(0, 50) + '...');
        this.qrCode = data.qrCode;
        this.emitEvent('qr', { qrCode: data.qrCode });
        console.log('📱 QR Event emitido correctamente desde qr-code');
      } else {
        console.warn('⚠️ QR Code recibido pero está vacío');
      }
    });

    // Evento QR alternativo
    this.socket.on('qr', (data) => {
      console.log('📱 QR Code recibido del backend (evento qr):', data);
      console.log('📱 ID Empresa:', data.idEmpresa);
      console.log('📱 QR Data exists:', !!data.qrCode);
      
      if (data.qrCode) {
        console.log('📱 QR Code preview:', data.qrCode.substring(0, 50) + '...');
        this.qrCode = data.qrCode;
        this.emitEvent('qr', { qrCode: data.qrCode });
        console.log('📱 QR Event emitido correctamente desde qr');
      }
    });

    // Evento QR adicional
    this.socket.on('whatsapp-qr', (data) => {
      console.log('📱 QR Code recibido del backend (evento whatsapp-qr):', data);
      console.log('📱 ID Empresa:', data.idEmpresa);
      
      if (data.qrCode) {
        console.log('📱 QR Code preview:', data.qrCode.substring(0, 50) + '...');
        this.qrCode = data.qrCode;
        this.emitEvent('qr', { qrCode: data.qrCode });
        console.log('📱 QR Event emitido correctamente desde whatsapp-qr');
      }
    });

    this.socket.on('ready', (data) => {
      console.log('✅ WhatsApp conectado exitosamente (desde backend)');
      console.log('✅ Client info:', data);
      this.isReady = true;
      this.isConnecting = false;
      this.connectionStatus = 'connected';
      this.qrCode = null;
      this.emitEvent('ready', { message: data.message });
    });

    this.socket.on('status-change', (data) => {
      console.log('🔄 Cambio de estado:', data);
      this.connectionStatus = data.status;
      this.emitEvent('statusChange', data);
    });

    this.socket.on('authenticated', (data) => {
      console.log('✅ WhatsApp autenticado:', data);
      this.emitEvent('authenticated', data);
    });

    this.socket.on('disconnected', (data) => {
      console.log('⚠️ WhatsApp desconectado:', data);
      this.isReady = false;
      this.isConnecting = false;
      this.connectionStatus = 'disconnected';
      this.qrCode = null;
      this.emitEvent('disconnected', data);
    });

    this.socket.on('error', (data) => {
      console.error('❌ Error en WhatsApp:', data);
      this.lastError = data.error;
      this.connectionStatus = 'error';
      this.isConnecting = false;
      this.emitEvent('error', data);
    });

    this.socket.on('message-received', (data) => {
      console.log('📨 ¡MENSAJE RECIBIDO EN FRONTEND!');
      console.log('📨 Timestamp:', new Date().toISOString());
      console.log('📨 Socket conectado:', this.socket.connected);
      console.log('📨 Socket ID:', this.socket.id);
      console.log('📨 Datos del mensaje:', {
        from: data.from,
        fromName: data.fromName,
        body: data.body,
        idEmpresa: data.idEmpresa,
        timestamp: data.timestamp
      });
      
      this.emitEvent('messageReceived', data);
      console.log('📨 Evento messageReceived emitido al componente Vue');
    });

    this.socket.on('bulk-progress', (data) => {
      console.log('📊 Progreso bulk:', data);
      this.emitEvent('bulkProgress', data);
    });

    this.socket.on('loading', (data) => {
      console.log('⏳ Loading:', data);
      this.emitEvent('loading', data);
    });

    // Eventos de debug adicionales
    this.socket.onAny((eventName, ...args) => {
      console.log('🔍 Socket.IO event received:', eventName, args);
    });
  }

  // Monitorear conexión Socket.IO periódicamente
  startConnectionMonitoring() {
    console.log('🔍 Iniciando monitoreo de conexión Socket.IO...');
    
    // Verificar conexión cada 30 segundos
    this.connectionInterval = setInterval(() => {
      const now = new Date().toISOString();
      console.log(`🔍 [${now}] Estado Socket.IO:`, {
        connected: this.socket.connected,
        disconnected: this.socket.disconnected,
        socketId: this.socket.id || 'N/A'
      });
      
      // Si no está conectado, intentar reconectar
      if (!this.socket.connected && (this.socket?.opts?.reconnection !== false)) {
        console.log('🔄 Socket no conectado, forzando reconexión...');
        this.socket.connect();
      }
    }, 30000); // 30 segundos
  }

  // Detener monitoreo de conexión
  stopConnectionMonitoring() {
    if (this.connectionInterval) {
      clearInterval(this.connectionInterval);
      this.connectionInterval = null;
      console.log('🔍 Monitoreo de conexión detenido');
    }
  }

  // Inicializar WhatsApp (conectar al backend)
  async initialize(idEmpresa) {
    console.log('🚀 WhatsApp Service - Initialize iniciado');
    console.log('🏢 ID Empresa recibido:', idEmpresa);
    console.log('📊 Estado actual del servicio:', {
      isConnecting: this.isConnecting,
      isReady: this.isReady,
      connectionStatus: this.connectionStatus,
      socketConnected: this.socket.connected
    });

    if (!idEmpresa) {
      console.error('❌ ID de empresa no proporcionado');
      return { success: false, message: 'ID de empresa es requerido' };
    }

    if (this.isConnecting || this.isReady) {
      console.warn('⚠️ WhatsApp ya está inicializándose o conectado');
      return { success: false, message: 'WhatsApp ya está inicializándose o conectado' };
    }

    try {
      this.isConnecting = true;
      this.connectionStatus = 'connecting';
      this.qrCode = null; // Limpiar QR anterior
      this.emitEvent('statusChange', { status: 'connecting', message: 'Conectando al servidor WhatsApp...' });

      console.log(`🔗 URL del API: ${this.apiBaseUrl}`);
      console.log(`🚀 Conectando al servidor WhatsApp API para empresa ${idEmpresa}...`);
      
      // Asegurar conexión Socket.IO
      if (!this.socket.connected) {
        console.log('🔌 Socket.IO no conectado, iniciando conexión...');
        this.socket.connect();
        
        // Esperar a que Socket.IO se conecte
        console.log('⏳ Esperando conexión Socket.IO...');
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            console.error('❌ Timeout conectando Socket.IO (5s)');
            reject(new Error('Timeout conectando Socket.IO'));
          }, 10000); // Aumenté el timeout a 10 segundos
          
          if (this.socket.connected) {
            clearTimeout(timeout);
            console.log('✅ Socket.IO ya estaba conectado');
            resolve();
          } else {
            this.socket.once('connect', () => {
              clearTimeout(timeout);
              console.log('✅ Socket.IO conectado, procediendo...');
              resolve();
            });
            this.socket.once('connect_error', (error) => {
              clearTimeout(timeout);
              console.error('❌ Error conectando Socket.IO:', error);
              reject(error);
            });
          }
        });
      } else {
        console.log('✅ Socket.IO ya conectado, procediendo...');
      }

      // Llamar API REST para inicializar
      console.log('📡 Enviando petición de inicialización al backend...');
      console.log('📡 URL completa:', `${this.apiBaseUrl}/api/whatsapp/initialize`);
      
      // Hacer la petición de manera más asíncrona - no esperar toda la respuesta
      const initPromise = axios.post(`${this.apiBaseUrl}/api/whatsapp/initialize`, {
        idEmpresa
      }, {
        timeout: 15000, // Solo 15 segundos para la respuesta inicial
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Iniciar el proceso pero no esperar completamente
      try {
        const response = await initPromise;
        console.log('📝 Respuesta inicial del backend:', response.data);
        
        if (response.data.success) {
          console.log('✅ Inicialización iniciada exitosamente');
          console.log('⏳ El QR llegará por Socket.IO...');
          
          // Emitir evento de que estamos esperando QR
          this.emitEvent('statusChange', { 
            status: 'waiting-qr', 
            message: 'Conectando con WhatsApp Web... El QR aparecerá pronto.' 
          });
          
          return { 
            success: true, 
            message: 'Inicialización iniciada. Esperando código QR...',
            idEmpresa: response.data.idEmpresa
          };
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        // Si hay timeout, pero el proceso ya se inició, continuar esperando por Socket.IO
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          console.log('⏳ Timeout en HTTP pero proceso iniciado, esperando por Socket.IO...');
          
          this.emitEvent('statusChange', { 
            status: 'waiting-qr', 
            message: 'Proceso iniciado. Esperando código QR por Socket.IO...' 
          });
          
          return { 
            success: true, 
            message: 'Proceso iniciado. El QR llegará por Socket.IO.',
            idEmpresa: idEmpresa,
            note: 'Timeout en HTTP pero proceso continúa'
          };
        } else {
          throw error;
        }
      }

    } catch (error) {
      console.error('❌ Error en initialize:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        stack: error.stack
      });
      
      this.connectionStatus = 'error';
      this.lastError = error.message;
      this.isConnecting = false;
      this.emitEvent('error', { error: error.message });
      
      return { 
        success: false, 
        message: `Error: ${error.message}`,
        details: error.response?.data || error.message
      };
    }
  }

  // Emitir eventos a los listeners registrados
  emitEvent(eventName, data) {
    if (this.eventCallbacks[eventName]) {
      this.eventCallbacks[eventName].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error en callback del evento ${eventName}:`, error);
        }
      });
    }
  }

  // Registrar callback para eventos
  on(eventName, callback) {
    if (!this.eventCallbacks[eventName]) {
      this.eventCallbacks[eventName] = [];
    }
    this.eventCallbacks[eventName].push(callback);
  }

  // Remover callback de eventos
  off(eventName, callback) {
    if (this.eventCallbacks[eventName]) {
      this.eventCallbacks[eventName] = this.eventCallbacks[eventName].filter(cb => cb !== callback);
    }
  }

  // Enviar mensaje individual (via API backend)
  async sendMessage(idEmpresa, phoneNumber, message) {
    try {
      if (!idEmpresa) {
        return { success: false, message: 'ID de empresa es requerido' };
      }

      console.log(`📱 Enviando mensaje via API backend para empresa ${idEmpresa}...`);
      console.log(`📞 Para: ${phoneNumber}`);
      console.log(`💬 Mensaje: ${message}`);
      
      const response = await axios.post(`${this.apiBaseUrl}/api/whatsapp/send-message`, {
        idEmpresa,
        phoneNumber,
        message
      });
      
      if (response.data.success) {
        console.log('✅ Mensaje enviado exitosamente');
        return {
          success: true,
          message: response.data.message,
          messageId: response.data.messageId,
          queued: response.data.queued || false,
          idEmpresa: response.data.idEmpresa
        };
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('❌ Error enviando mensaje:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  }

  // Enviar mensaje con plantilla (via API backend)
  async sendTemplateMessage(idEmpresa, phoneNumber, templateType, data) {
    try {
      if (!idEmpresa) {
        return { success: false, message: 'ID de empresa es requerido' };
      }

      console.log(`📋 Enviando mensaje con plantilla via API backend para empresa ${idEmpresa}...`);
      console.log(`📞 Para: ${phoneNumber}`);
      console.log(`📋 Plantilla: ${templateType}`);
      
      const response = await axios.post(`${this.apiBaseUrl}/api/whatsapp/send-template`, {
        idEmpresa,
        phoneNumber,
        templateType,
        data
      });
      
      if (response.data.success) {
        console.log('✅ Mensaje con plantilla enviado exitosamente');
        return {
          success: true,
          message: response.data.message,
          messageId: response.data.messageId,
          templateType: response.data.templateType,
          generatedMessage: response.data.generatedMessage,
          idEmpresa: response.data.idEmpresa
        };
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('❌ Error enviando mensaje con plantilla:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  }

  // Enviar mensajes masivos (via API backend)
  async sendBulkMessages(idEmpresa, contacts, message, delay = 3000) {
    try {
      if (!idEmpresa) {
        return { success: false, message: 'ID de empresa es requerido' };
      }

      console.log(`📬 Enviando mensajes masivos via API backend para empresa ${idEmpresa}...`);
      console.log(`📬 Cantidad: ${contacts.length}`);
      
      const response = await axios.post(`${this.apiBaseUrl}/api/whatsapp/send-bulk`, {
        idEmpresa,
        contacts,
        message,
        delay
      });
      
      if (response.data.success) {
        console.log('✅ Envío masivo iniciado exitosamente');
        return {
          success: true,
          message: response.data.message,
          totalContacts: response.data.totalContacts,
          estimatedTime: response.data.estimatedTime,
          idEmpresa: response.data.idEmpresa
        };
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('❌ Error en envío masivo:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  }

  // Desconectar WhatsApp (via API backend)
  async disconnect(idEmpresa, disconnectSocket = false) {
    try {
      if (!idEmpresa) {
        return { success: false, message: 'ID de empresa es requerido' };
      }

      console.log(`🛑 Desconectando WhatsApp via API backend para empresa ${idEmpresa}...`);
      console.log(`🔌 ¿Desconectar Socket.IO también?`, disconnectSocket);
      
      const response = await axios.delete(`${this.apiBaseUrl}/api/whatsapp/disconnect`, {
        data: { idEmpresa }
      });
      
      // Solo desconectar Socket.IO si se solicita explícitamente
      if (disconnectSocket && this.socket.connected) {
        console.log('🔌 Desconectando Socket.IO...');
        this.socket.disconnect();
        this.stopConnectionMonitoring();
      } else {
        console.log('🔌 Manteniendo Socket.IO conectado para recibir mensajes...');
      }
      
      this.reset();
      
      if (response.data.success) {
        console.log('✅ WhatsApp desconectado correctamente');
        return { 
          success: true, 
          message: response.data.message,
          idEmpresa: response.data.idEmpresa
        };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('❌ Error desconectando WhatsApp:', error);
      this.reset();
      return { 
        success: false, 
        message: error.response?.data?.message || error.message 
      };
    }
  }

  // Resetear estado del servicio (sin desconectar Socket.IO)
  reset() {
    console.log('🔄 Reseteando estado del servicio WhatsApp...');
    this.isReady = false;
    this.isConnecting = false;
    this.qrCode = null;
    this.connectionStatus = 'disconnected';
    this.lastError = null;
    this.messageQueue = [];
    this.isProcessingQueue = false;
    // NO desconectar Socket.IO aquí - mantener para recibir mensajes
    console.log('✅ Estado reseteado - Socket.IO permanece conectado para mensajes');
  }

  // Obtener estado actual (desde API backend)
  async getStatus(idEmpresa) {
    try {
      if (!idEmpresa) {
        return {
          isReady: false,
          isConnecting: false,
          connectionStatus: 'error',
          qrCode: null,
          lastError: 'ID de empresa es requerido',
          queueLength: 0
        };
      }

      const response = await axios.get(`${this.apiBaseUrl}/api/whatsapp/status/${idEmpresa}`);
      
      if (response.data.success) {
        // Actualizar estado local
        this.isReady = response.data.data.isReady;
        this.isConnecting = response.data.data.isConnecting;
        this.connectionStatus = response.data.data.connectionStatus;
        this.qrCode = response.data.data.qrCode;
        this.lastError = response.data.data.lastError;
        
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('❌ Error obteniendo estado:', error);
      return {
        isReady: false,
        isConnecting: false,
        connectionStatus: 'error',
        qrCode: null,
        lastError: error.message,
        queueLength: 0,
        idEmpresa
      };
    }
  }

  // Obtener estado de todas las empresas (desde API backend)
  async getAllStatus() {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/api/whatsapp/status`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('❌ Error obteniendo estado de todas las empresas:', error);
      return {
        totalCompanies: 0,
        companies: []
      };
    }
  }

  // Verificar si un número existe en WhatsApp (via API backend)
  async checkNumberExists(idEmpresa, phoneNumber) {
    try {
      if (!idEmpresa) {
        return { exists: false, error: 'ID de empresa es requerido' };
      }

      console.log(`🔍 Verificando número via API backend para empresa ${idEmpresa}...`);
      
      const response = await axios.post(`${this.apiBaseUrl}/api/whatsapp/check-number`, {
        idEmpresa,
        phoneNumber
      });
      
      if (response.data.success) {
        console.log(`✅ Número ${phoneNumber} ${response.data.exists ? 'SÍ' : 'NO'} está registrado en WhatsApp`);
        return {
          exists: response.data.exists,
          number: response.data.number,
          idEmpresa: response.data.idEmpresa
        };
      } else {
        return { exists: false, error: response.data.error };
      }
    } catch (error) {
      console.error('❌ Error verificando número:', error);
      return { 
        exists: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  }

  // Método de diagnóstico para probar conexiones
  async runDiagnostics(idEmpresa) {
    console.log('🩺 Iniciando diagnóstico de WhatsApp...');
    
    const diagnostics = {
      timestamp: new Date().toISOString(),
      idEmpresa: idEmpresa,
      tests: {},
      summary: {
        total: 0,
        passed: 0,
        failed: 0
      }
    };

    // Test 1: Verificar configuración
    console.log('🔍 Test 1: Verificando configuración...');
    try {
      diagnostics.tests.configuration = {
        name: 'Configuración',
        status: 'passed',
        details: {
          apiBaseUrl: this.apiBaseUrl,
          socketConnected: this.socket.connected,
          socketId: this.socket.id
        }
      };
      diagnostics.summary.passed++;
    } catch (error) {
      diagnostics.tests.configuration = {
        name: 'Configuración',
        status: 'failed',
        error: error.message
      };
      diagnostics.summary.failed++;
    }
    diagnostics.summary.total++;

    // Test 2: Verificar conexión Socket.IO
    console.log('🔍 Test 2: Verificando conexión Socket.IO...');
    try {
      if (!this.socket.connected) {
        console.log('🔌 Socket.IO no conectado, intentando conectar...');
        this.socket.connect();
        
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout conectando Socket.IO'));
          }, 5000);
          
          if (this.socket.connected) {
            clearTimeout(timeout);
            resolve();
          } else {
            this.socket.once('connect', () => {
              clearTimeout(timeout);
              resolve();
            });
            this.socket.once('connect_error', (error) => {
              clearTimeout(timeout);
              reject(error);
            });
          }
        });
      }
      
      diagnostics.tests.socketConnection = {
        name: 'Conexión Socket.IO',
        status: 'passed',
        details: {
          connected: this.socket.connected,
          socketId: this.socket.id,
          transport: this.socket.io.engine.transport.name
        }
      };
      diagnostics.summary.passed++;
    } catch (error) {
      diagnostics.tests.socketConnection = {
        name: 'Conexión Socket.IO',
        status: 'failed',
        error: error.message
      };
      diagnostics.summary.failed++;
    }
    diagnostics.summary.total++;

    // Test 3: Verificar API REST
    console.log('🔍 Test 3: Verificando API REST...');
    try {
      const response = await axios.get(`${this.apiBaseUrl}/api/whatsapp/status`, {
        timeout: 10000
      });
      
      diagnostics.tests.apiRest = {
        name: 'API REST',
        status: 'passed',
        details: {
          responseStatus: response.status,
          responseTime: response.config.timeout,
          serverOnline: response.data.success
        }
      };
      diagnostics.summary.passed++;
    } catch (error) {
      diagnostics.tests.apiRest = {
        name: 'API REST',
        status: 'failed',
        error: error.message,
        details: {
          code: error.code,
          response: error.response?.status
        }
      };
      diagnostics.summary.failed++;
    }
    diagnostics.summary.total++;

    // Test 4: Verificar estado específico de empresa (si hay ID)
    if (idEmpresa) {
      console.log('🔍 Test 4: Verificando estado de empresa...');
      try {
        const response = await axios.get(`${this.apiBaseUrl}/api/whatsapp/status/${idEmpresa}`, {
          timeout: 10000
        });
        
        diagnostics.tests.companyStatus = {
          name: 'Estado de Empresa',
          status: 'passed',
          details: {
            idEmpresa: idEmpresa,
            whatsappReady: response.data.data?.isReady,
            whatsappConnecting: response.data.data?.isConnecting,
            connectionStatus: response.data.data?.connectionStatus
          }
        };
        diagnostics.summary.passed++;
      } catch (error) {
        diagnostics.tests.companyStatus = {
          name: 'Estado de Empresa',
          status: 'failed',
          error: error.message
        };
        diagnostics.summary.failed++;
      }
      diagnostics.summary.total++;
    }

    // Mostrar resultados
    console.log('📊 Resultados de diagnóstico:', diagnostics);
    
    const success = diagnostics.summary.failed === 0;
    const message = success 
      ? `✅ Todos los tests pasaron (${diagnostics.summary.passed}/${diagnostics.summary.total})`
      : `⚠️ ${diagnostics.summary.failed} tests fallaron de ${diagnostics.summary.total}`;

    return {
      success,
      message,
      diagnostics
    };
  }
}

// Exportar instancia única (singleton)
export default new WhatsAppService(); 